import type { JSX } from "react";
import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ContactDetailKind } from "@/services/quoteRequestsService";

interface ContactBlockedNoticeProps {
  kinds: ContactDetailKind[];
  /**
   * What was not sent, in the words of the surface the author is on — a
   * message, a quote, a request. Only the first line changes; the reason and
   * the instruction are the same everywhere, because the rule is.
   */
  titleKey?: string;
}

/**
 * Why a piece of text was refused, shown inline next to what the author wrote.
 *
 * Inline rather than a toast on purpose: moderation refusing a message is a
 * normal outcome of these forms, not a failure of them, and the author needs
 * the reason to still be on screen while they edit.
 *
 * Shared by all three moderated surfaces — the clarification thread, the quote
 * submission form and the RFQ wizard — so the same refusal cannot end up
 * looking like three different problems. The copy still lives under
 * `requests.clarifications.*`, which is where it was written when
 * clarifications were the only moderated channel.
 */
export function ContactBlockedNotice({
  kinds,
  titleKey = "requests.clarifications.blockedTitle",
}: ContactBlockedNoticeProps): JSX.Element {
  const { t } = useTranslation();
  const labels = kinds.map((k) => t(`requests.clarifications.blockedKind_${k}`));
  return (
    <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-medium text-destructive">{t(titleKey)}</p>
        <p className="text-xs text-muted-foreground">
          {labels.length > 0
            ? t("requests.clarifications.blockedDetected", { kinds: labels.join(", ") })
            : t("requests.clarifications.blockedGeneric")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("requests.clarifications.blockedRephrase")}
        </p>
      </div>
    </div>
  );
}
